import {Context} from '@datadog/browser-core';
import {DateTime} from 'luxon';
import uuid from 'uuid-random';

import {globalWindow} from '../../../../../libs/shared/helpers/src/browser/browserHelpers';
import {logger} from '../../../../../srcCommon/services/logger/logger';
import {LogLevelsEnum} from '../../../../../srcCommon/services/logger/loggerCommon';
import {
  AnalyticsReportsEnum,
  getReportOptionsWithBucketBoundaries,
} from '../../../../analytics/helpers/analyticsReportHelpers';
import {CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {ActionBase, ActionWithPayload} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsJobProgressEvent,
  AnalyticsJobResult,
  AnalyticsJobStatusEnum,
  ApiAnalyticsJobResult,
} from '../../../../models/api/analyticsJobsModel';
import {AnalyticsMetricsKeys} from '../../../../models/api/analyticsMetricsModel';
import {
  AnalyticsOptionsBucketBoundaries,
  AnalyticsReportFilterOptions,
  AnalyticsSelectedMetricsModel,
} from '../../../../models/api/analyticsSavedViewModel';
import {fetchApiAsync, postApiAsync} from '../../../../services/http/http';
import {AppState} from '../../../appState';
import {ThunkAction, ThunkAsyncAction} from '../../../appThunk';
import {getCompany} from '../../../data/company/companySelectors';
import {buildAnalyticsQueryFromState, makeAnalyticsApiPath} from '../analyticsPanelHelpers';
import {setIsSidePanelOpenAction} from '../details/analyticsPanelDetailsActions';
import {
  getAnalyticsReportType,
  getAnalyticsSelectedNamespace,
} from '../parameters/analyticsPanelParametersSelectors';
import {
  getAnalyticsBucketBoundaries,
  getAnalyticsMetricsToFetch,
  getAnalyticsReportJobs,
} from './analyticsPanelReportSelectors';
import {AnalyticsReportJobPrioritiesEnum} from './analyticsPanelReportState';

/*
 * Constants.
 */

const analyticsLogger = logger.getLogger('analytics');

const JOB_DURATION_BEFORE_STUCK = 20000; // 20s without events is suspicious.

/*
 * Type enum.
 */

export enum AnalyticsPanelReportActionsEnum {
  SET_REPORT_TYPE = '@@panels/analytics/report/SET_REPORT_TYPE',
  RESET_JOBS = '@@panels/analytics/report/RESET_JOBS',
  FETCH_ANALYTICS_METRICS = '@@panels/analytics/FETCH_ANALYTICS_METRICS',
  FETCH_ANALYTICS_METRICS_COMPLETED = '@@panels/analytics/FETCH_ANALYTICS_METRICS_COMPLETED',
  FETCH_ANALYTICS_METRICS_FAILED = '@@panels/analytics/FETCH_ANALYTICS_METRICS_FAILED',
  UPDATE_ANALYTICS_JOBS = '@@panels/analytics/UPDATE_ANALYTICS_JOBS',
  UPDATE_SELECTED_METRICS = '@@panels/analytics/views/UPDATE_SELECTED_METRICS',
  UPDATE_BUCKET_BOUNDARIES = '@@panels/analytics/views/UPDATE_BUCKET_BOUNDARIES',
  SET_REPORT_OPTIONS = '@@panels/analytics/views/UPDATE_REPORT_OPTIONS',
}

/*
 * Types.
 */

interface SetReportTypeAction extends ActionWithPayload<AnalyticsReportsEnum> {
  type: AnalyticsPanelReportActionsEnum.SET_REPORT_TYPE;
}
interface ResetJobsAction extends ActionBase {
  type: AnalyticsPanelReportActionsEnum.RESET_JOBS;
}

interface FetchAnalyticsMetricsActionPayload {
  metrics: ReadonlyArray<AnalyticsMetricsKeys>;
  priority: AnalyticsReportJobPrioritiesEnum;
  localId: string; // Will be set on the created job.
}

interface FetchAnalyticsMetricsAction extends ActionWithPayload<FetchAnalyticsMetricsActionPayload> {
  type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS;
}

interface FetchAnalyticsMetricsCompletedActionPayload extends AnalyticsJobResult {
  jobLocalId?: string;
}

interface FetchAnalyticsMetricsCompletedAction
  extends ActionWithPayload<FetchAnalyticsMetricsCompletedActionPayload> {
  type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_COMPLETED;
}

interface FetchAnalyticsMetricsFailedActionPayload {
  jobUid?: string;
  jobLocalId?: string;
  metrics?: ReadonlyArray<AnalyticsMetricsKeys>;
  error: string;
}

interface FetchAnalyticsMetricsFailedAction
  extends ActionWithPayload<FetchAnalyticsMetricsFailedActionPayload> {
  type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_FAILED;
}

interface UpdateAnalyticsMetricsJobProgressActionPayload {
  jobUid: string;
  jobLocalId?: string;
  metrics?: ReadonlyArray<AnalyticsMetricsKeys>;
  progress: number;
  startTime?: DateTime;
}

interface UpdateAnalyticsMetricsJobProgressAction
  extends ActionWithPayload<UpdateAnalyticsMetricsJobProgressActionPayload> {
  type: AnalyticsPanelReportActionsEnum.UPDATE_ANALYTICS_JOBS;
}

interface UpdateSelectedMetricsAction extends ActionWithPayload<AnalyticsSelectedMetricsModel> {
  type: AnalyticsPanelReportActionsEnum.UPDATE_SELECTED_METRICS;
}

interface UpdateBucketBoundariesAction extends ActionWithPayload<AnalyticsOptionsBucketBoundaries> {
  type: AnalyticsPanelReportActionsEnum.UPDATE_BUCKET_BOUNDARIES;
}

interface SetReportOptionsAction extends ActionWithPayload<AnalyticsReportFilterOptions> {
  type: AnalyticsPanelReportActionsEnum.SET_REPORT_OPTIONS;
}

export type FetchAnalyticsMetricsAsyncThunkAction = ThunkAsyncAction<
  | FetchAnalyticsMetricsCompletedAction
  | FetchAnalyticsMetricsFailedAction
  | UpdateAnalyticsMetricsJobProgressAction
>;

/*
 * Action creators.
 */

export function setReportTypeAction(reportType: AnalyticsReportsEnum): FetchAnalyticsMetricsAsyncThunkAction {
  return (dispatch) => {
    // When changing report, we close the details panel.
    dispatch(setIsSidePanelOpenAction(false));

    dispatch({
      type: AnalyticsPanelReportActionsEnum.SET_REPORT_TYPE,
      payload: reportType,
    });

    return dispatch(fetchVisibleMetricsForReport());
  };
}

export function fetchMetricWithBucketBoundariesAction(
  metric: AnalyticsMetricsKeys,
  bucketBoundaries: AnalyticsOptionsBucketBoundaries,
): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const reportType = getAnalyticsReportType(state);

    const options = getReportOptionsWithBucketBoundaries(reportType, [metric], bucketBoundaries);

    // Only fetch the metric this bucket_boundary is for
    return dispatch(fetchAnalyticsMetrics([metric], AnalyticsReportJobPrioritiesEnum.SECONDARY, options));
  };
}

export function fetchMetricsSecondaryAction(metrics: ReadonlyArray<AnalyticsMetricsKeys>): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const reportType = getAnalyticsReportType(state);
    const bucketBoundaries = getAnalyticsBucketBoundaries(state);

    const options = getReportOptionsWithBucketBoundaries(reportType, [...metrics], bucketBoundaries);

    return dispatch(fetchAnalyticsMetrics(metrics, AnalyticsReportJobPrioritiesEnum.SECONDARY, options));
  };
}

// Will fetch the visible metrics of a report. This is called on first load, when changing filters, report type, period or namespace.
export function fetchVisibleMetricsForReport(): FetchAnalyticsMetricsAsyncThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const {displayedMetrics} = getAnalyticsMetricsToFetch(state);
    const reportType = getAnalyticsReportType(state);
    const bucketBoundaries = getAnalyticsBucketBoundaries(state);

    const options = getReportOptionsWithBucketBoundaries(reportType, displayedMetrics, bucketBoundaries);

    // We clean the existing jobs to avoid starting multiple queries. A new job will be created based on the selected metrics.
    // TODO Cancel running jobs when this is supported by the backend.
    dispatch({
      type: AnalyticsPanelReportActionsEnum.RESET_JOBS,
    });

    // Only fetch visible metrics. Hidden metrics are fetched on demand.
    return dispatch(
      fetchAnalyticsMetrics(displayedMetrics, AnalyticsReportJobPrioritiesEnum.PRIMARY, options),
    );
  };
}

export function fetchAnalyticsMetrics(
  metrics: ReadonlyArray<AnalyticsMetricsKeys>,
  priority: AnalyticsReportJobPrioritiesEnum,
  options: AnalyticsReportFilterOptions = {},
  cancelToken?: CancelToken,
): FetchAnalyticsMetricsAsyncThunkAction {
  return async (dispatch, getState) => {
    // If we have to create a new job, jobLocalId will be attached to it and used to update the job metrics.
    // That's because if the report data is returned synchronously, we won't have a job uid so we need the local id to find the job in the store.
    const newJobLocalId = uuid();

    // State.
    const state = getState();
    const company = getCompany(state);
    const namespace = getAnalyticsSelectedNamespace(state);

    const reportType = getAnalyticsReportType(state);

    // Build the query.
    const query = buildAnalyticsQueryFromState(state, {metrics}, options);
    const {path} = makeAnalyticsMetricsApiPath(company.url, namespace, reportType);

    dispatch({
      type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS,
      payload: {metrics, priority, localId: newJobLocalId},
    });

    try {
      // Initiate a fetch from the network.
      const reportDataPromise = postApiAsync(ApiAnalyticsJobResult, path, query, {cancelToken});
      const reportData = await reportDataPromise;

      logMessage(LogLevelsEnum.INFO, '[Analytics] Report requested', {
        reportType,
        jobUid: reportData.uid,
        jobPriority: priority,
      });

      // If the status is `done`, it means that the report was generated synchronously.
      // Thus, we can dispatch the completed action directly.
      if (reportData.status === AnalyticsJobStatusEnum.DONE) {
        logMessage(LogLevelsEnum.INFO, '[Analytics] Metrics fetched synchronously', {
          reportType,
          jobUid: reportData.uid,
          jobPriority: priority,
        });

        return dispatch({
          type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_COMPLETED,
          payload: {...reportData, jobLocalId: newJobLocalId},
        });
      }

      // If the status is `error`, it means something went wrong in the BE.
      if (reportData.status === AnalyticsJobStatusEnum.ERROR) {
        logMessage(LogLevelsEnum.ERROR, '[Analytics] Report request failed', {
          reportType,
          jobUid: reportData.uid,
          jobPriority: priority,
        });

        return dispatch({
          type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_FAILED,
          payload: {
            uid: reportData.uid,
            metrics,
            error: 'ERROR FROM THE JOB', // @TODO: Parse errors from the BE.
          },
        });
      }

      startLogTimeout(getState, reportData.uid);

      // Otherwise, we're just waiting for the BE to process the data.
      return dispatch({
        type: AnalyticsPanelReportActionsEnum.UPDATE_ANALYTICS_JOBS,
        payload: {
          jobUid: reportData.uid,
          jobLocalId: newJobLocalId,
          progress: reportData.progress,
          startTime: reportData.startTime,
        },
      });
    } catch (err) {
      if (err?.status !== 403) {
        logMessage(LogLevelsEnum.ERROR, '[Analytics] Fetch error', {
          reportType,
          err,
        });
      }

      return dispatch({
        type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_FAILED,
        payload: {
          metrics,
          error: 'Something wrong happened while retrieving metrics!',
        },
      });
    }
  };
}

function fetchAnalyticsMetricsFromJob(
  jobUid: string,
): ThunkAsyncAction<
  | FetchAnalyticsMetricsCompletedAction
  | FetchAnalyticsMetricsFailedAction
  | UpdateAnalyticsMetricsJobProgressAction
> {
  return async (dispatch, getState) => {
    // State.
    const state = getState();
    const company = getCompany(state);
    const namespace = getAnalyticsSelectedNamespace(state);

    const reportType = getAnalyticsReportType(state);

    maybeClearLogTimeout(jobUid);

    // Build the query.
    const {path} = makeAnalyticsMetricsApiPath(company.url, namespace, reportType, jobUid);

    /**
     * This is called from 2 places:
     * - from processAnalyticsJobProgressEventAction when the job is DONE. In that case we have the report metrics and populate the store.
     * - from onAnalyticsRoomSubscriptionAction after having subscribed to the pusher room as a protection against race conditions
     *  More details https://github.com/frontapp/front-client/pull/5885
     */

    try {
      // Initiate a fetch from the network.
      const reportDataPromise = fetchApiAsync(ApiAnalyticsJobResult, path);
      const reportData = await reportDataPromise;

      // Report DONE, we have the metrics.
      if (reportData.status === AnalyticsJobStatusEnum.DONE) {
        logMessage(LogLevelsEnum.INFO, '[Analytics] Job completed, report fetched', {
          reportType,
          jobUid,
        });
        return dispatch({
          type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_COMPLETED,
          payload: reportData,
        });
      }
      // Else, report isn't completed yet but the room connection is set - we still update the report progress with the response we got.
      logMessage(LogLevelsEnum.INFO, '[Analytics] Job not completed yet, room synced.', {
        reportType,
        jobUid,
      });

      return dispatch({
        type: AnalyticsPanelReportActionsEnum.UPDATE_ANALYTICS_JOBS,
        payload: {
          jobUid: reportData.uid,
          progress: reportData.progress,
        },
      });
    } catch (err) {
      logMessage(LogLevelsEnum.ERROR, '[Analytics] Error while fetching metrics from job', {
        error: err,
      });
      return dispatch({
        type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_FAILED,
        payload: {
          jobUid,
          error: 'Something wrong happened while retrieving metrics!',
        },
      });
    }
  };
}

export function processAnalyticsJobProgressEventAction(
  jobProgressEvent: AnalyticsJobProgressEvent,
): ThunkAsyncAction<
  | FetchAnalyticsMetricsCompletedAction
  | FetchAnalyticsMetricsFailedAction
  | UpdateAnalyticsMetricsJobProgressAction
  | undefined
> {
  return async (dispatch, getState) => {
    const state = getState();
    const reportType = getAnalyticsReportType(state);

    logMessage(LogLevelsEnum.INFO, '[Analytics] Process analytics job progress', {
      reportType,
      jobUid: jobProgressEvent.uid,
      jobProgress: jobProgressEvent.progress,
      jobStatus: jobProgressEvent.status,
    });

    startLogTimeout(getState, jobProgressEvent.uid);

    // If the status is `done`, it means the BE has finished processing the report.
    if (jobProgressEvent.status === AnalyticsJobStatusEnum.DONE) {
      return dispatch(fetchAnalyticsMetricsFromJob(jobProgressEvent.uid));
    }

    // If the status is `error`, it means something went wrong in the BE.
    if (jobProgressEvent.status === AnalyticsJobStatusEnum.ERROR) {
      return dispatch({
        type: AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_FAILED,
        payload: {
          jobUid: jobProgressEvent.uid,
          error: 'ERROR FROM THE JOB', // @TODO: Parse errors from the BE.
        },
      });
    }

    // Otherwise, we're still waiting for the BE to process the data.
    return dispatch({
      type: AnalyticsPanelReportActionsEnum.UPDATE_ANALYTICS_JOBS,
      payload: {
        jobUid: jobProgressEvent.uid,
        progress: jobProgressEvent.progress,
        startTime: jobProgressEvent.startTime,
      },
    });
  };
}

export function updateAnalyticsSelectedMetrics(
  metrics: AnalyticsSelectedMetricsModel,
): UpdateSelectedMetricsAction {
  return {
    type: AnalyticsPanelReportActionsEnum.UPDATE_SELECTED_METRICS,
    payload: metrics,
  };
}

export function updateAnalyticsBucketBoundaries(
  bucketBoundaries: AnalyticsOptionsBucketBoundaries,
): UpdateBucketBoundariesAction {
  return {
    type: AnalyticsPanelReportActionsEnum.UPDATE_BUCKET_BOUNDARIES,
    payload: bucketBoundaries,
  };
}

export function setAnalyticsReportOptions(options: AnalyticsReportFilterOptions): SetReportOptionsAction {
  return {
    type: AnalyticsPanelReportActionsEnum.SET_REPORT_OPTIONS,
    payload: options,
  };
}

export function onAnalyticsRoomSubscriptionAction(room: string): ThunkAction {
  return (dispatch, getState) => {
    const state = getState();
    const jobs = getAnalyticsReportJobs(state);

    const [, jobUid] = room.split('-'); // "room" is of format analyticsreport-xxxxx.

    if (!jobUid) {
      return;
    }

    // Do we have a job with this uid?
    if (!jobs.some((job) => job.jobUid === jobUid)) {
      return;
    }

    dispatch(fetchAnalyticsMetricsFromJob(jobUid));
  };
}

/*
 * Union type.
 */

export type AnalyticsPanelReportAction =
  | SetReportTypeAction
  | ResetJobsAction
  | FetchAnalyticsMetricsAction
  | FetchAnalyticsMetricsCompletedAction
  | FetchAnalyticsMetricsFailedAction
  | UpdateAnalyticsMetricsJobProgressAction
  | UpdateSelectedMetricsAction
  | UpdateBucketBoundariesAction
  | SetReportOptionsAction;

/*
 * Helpers.
 */

/**
 * Making a POST request to the endpoint generated with the `getAnalyticsReportApiPath` function
 * may not necessarily return metrics right away.
 *
 * The whole workflow is described in the description of this PR:
 * https://github.com/frontapp/front-client/pull/4932
 */

function makeAnalyticsMetricsApiPath(
  companyUrl: string,
  selectedNamespace: string,
  view: string,
  jobUid?: string,
) {
  let metricsPath = `${makeAnalyticsApiPath(companyUrl, selectedNamespace)}/metrics/${view}`;
  let method = 'POST';

  if (jobUid) {
    metricsPath += `/${jobUid}`;
    method = 'GET';
  }

  return {
    path: `${metricsPath}`,
    method,
  };
}

/**
 * For each job that we start - associate a timeout with it.
 * If no events for a specific job is received after JOB_DURATION_BEFORE_STUCK ms and it is still loading, we assume the job is stuck.
 */

// Each job has its own timeout.
const logTimeouts: Record<string, number> = {};

function startLogTimeout(getState: () => AppState, jobUid: string) {
  // Clear the timeout if one already exists.
  maybeClearLogTimeout(jobUid);

  // Will log a warning if nothing happens for the next JOB_DURATION_BEFORE_STUCK ms and the job is still loading.
  logTimeouts[jobUid] = globalWindow.setInstrumentedTimeout(() => {
    const state = getState();
    const job = getAnalyticsReportJobs(state).find((j) => j.jobUid === jobUid);
    const reportType = getAnalyticsReportType(state);

    if (!job?.isLoading) {
      return;
    }

    logMessage(LogLevelsEnum.WARN, '[Analytics] Job seems stuck', {
      reportType,
      jobUid: job.jobUid,
      jobPriority: job.priority,
      jobProgress: job.progress,
    });
  }, JOB_DURATION_BEFORE_STUCK);
}

function maybeClearLogTimeout(jobUid: string) {
  if (logTimeouts[jobUid]) {
    globalWindow.clearTimeout(logTimeouts[jobUid]);
    delete logTimeouts[jobUid];
  }
}

function logMessage(severity: LogLevelsEnum, message: string, body: Context) {
  analyticsLogger[severity](message, body);
}
